import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  FormControlLabel,
  Alert,
  Paper,
  Divider,
  CircularProgress,
  Checkbox,
  IconButton,
  InputAdornment,
  Autocomplete,
  Chip,
  Card,
  CardActionArea
} from '@mui/material';
import { PersonOutlined, BusinessCenterOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { trackRegister } from '../../services/analyticsService';
import ReCAPTCHA from 'react-google-recaptcha';
import { HUNGARIAN_COUNTIES } from '../../constants/counties';

// YUP VALIDÁCIÓS SÉMA - FRISSÍTVE a backend szabályaihoz
const schema = yup.object().shape({
  firstName: yup.string()
    .trim()
    .required('A keresztnév megadása kötelező')
    .min(2, 'A keresztnév legalább 2 karakter hosszú legyen')
    .max(50, 'A keresztnév maximum 50 karakter hosszú lehet'),
  lastName: yup.string()
    .trim()
    .required('A vezetéknév megadása kötelező')
    .min(2, 'A vezetéknév legalább 2 karakter hosszú legyen')
    .max(50, 'A vezetéknév maximum 50 karakter hosszú lehet'),
  email: yup.string()
    .email('Érvényes email cím szükséges')
    .required('Az email cím megadása kötelező'),
  password: yup.string()
    .required('A jelszó megadása kötelező')
    .min(6, 'A jelszónak legalább 6 karakter hosszúnak kell lennie')
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'A jelszó tartalmazzon kis- és nagybetűt, valamint számot'),
  confirmPassword: yup.string()
    .required('A jelszó megerősítése kötelező')
    .oneOf([yup.ref('password'), null], 'A jelszavak nem egyeznek'),
  phone: yup.string()
    .optional()
    .test('phone-format', 'Érvényes magyar mobiltelefonszám szükséges (pl. +36201234567)', (value) => {
      if (!value) return true;
      return /^\+36(20|30|70)\d{7}$/.test(value);
    }),
  businessName: yup.string().when('userType', {
    is: 'PROVIDER',
    then: (schema) => schema
      .trim()
      .required('A vállalkozás neve kötelező szolgáltatóknak')
      .min(2, 'A vállalkozás neve legalább 2 karakter hosszú legyen')
      .max(100, 'A vállalkozás neve maximum 100 karakter hosszú lehet'),
    otherwise: (schema) => schema.optional(), // Ha nem PROVIDER, opcionális
  }),
  counties: yup.array().when('userType', {
    is: 'PROVIDER',
    then: (schema) => schema
      .of(yup.string())
      .min(1, 'Legalább egy megyét ki kell választani')
      .required('A megye megadása kötelező szolgáltatóknak'),
    otherwise: (schema) => schema.optional(),
  }),
  city: yup.string().when('userType', {
    is: 'PROVIDER',
    then: (schema) => schema
      .optional()
      .test('city-length', 'A város neve legalább 2 és legfeljebb 50 karakter lehet', val => !val || (val.trim().length >= 2 && val.trim().length <= 50)),
    otherwise: (schema) => schema.optional(),
  }),
  zipCode: yup.string().when('userType', {
    is: 'PROVIDER',
    then: (schema) => schema
      .optional()
      .test('zip-format', 'Az irányítószám 4 számjegyből áll', val => !val || /^\d{4}$/.test(val)),
    otherwise: (schema) => schema.optional(),
  }),
  userType: yup.string()
    .oneOf(['CLIENT', 'PROVIDER'], 'Érvénytelen felhasználó típus')
    .required('Felhasználó típus megadása kötelező'),
  subscribedToNewsletter: yup.boolean().optional(),
  privacyConsent: yup.boolean()
    .oneOf([true], 'Az adatkezelési tájékoztató elfogadása kötelező')
    .required('Az adatkezelési tájékoztató elfogadása kötelező'),
  termsConsent: yup.boolean()
    .oneOf([true], 'A Felhasználási Feltételek elfogadása kötelező')
    .required('A Felhasználási Feltételek elfogadása kötelező'),
});

const Register = () => {
  const { register: authRegister, error, validationErrors, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const [serverError, setServerError] = useState('');
  const [showValidationSummary, setShowValidationSummary] = useState(false);
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { handleSubmit, control, formState: { errors, isSubmitting }, setError, watch, setValue, clearErrors: clearFormErrors } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      phone: '',
      userType: 'CLIENT',
      businessName: '',
      city: '',
      zipCode: '',
      counties: [],
      subscribedToNewsletter: false,
      privacyConsent: false,
      termsConsent: false,
    }
  });

  const watchedUserType = watch('userType');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const userType = searchParams.get('userType');
    if (userType === 'PROVIDER') {
      setValue('userType', 'PROVIDER');
    }
  }, [searchParams, setValue]);

  useEffect(() => {
    clearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (validationErrors && validationErrors.length > 0) {
      validationErrors.forEach(err => {
        setError(err.path, { type: 'backend', message: err.msg });
      });
    }
  }, [validationErrors, setError]);

  const onValidationError = () => {
    setShowValidationSummary(true);
    const firstInvalid = document.querySelector('[aria-invalid="true"]');
    if (firstInvalid) {
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const onSubmit = async (formData) => {
    if (!captchaToken) {
      setCaptchaError('A captcha kitöltése kötelező');
      return;
    }
    setCaptchaError('');
    setServerError('');
    setShowValidationSummary(false);
    clearError();
    clearFormErrors();

    try {
      let userDataToSend = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        userType: formData.userType,
        captchaToken,
        subscribedToNewsletter: formData.subscribedToNewsletter,
      };

      if (formData.userType === 'PROVIDER') {
        userDataToSend = {
          ...userDataToSend,
          businessName: formData.businessName,
          city: formData.city,
          zipCode: formData.zipCode,
          county: formData.counties.join(','),
        };
      }

      const response = await authRegister(userDataToSend);

      if (response.success) {
        trackRegister();
        navigate('/activate', {
          state: {
            email: formData.email,
            justRegistered: true,
          }
        });
        return;
      }

      recaptchaRef.current?.reset();
      setCaptchaToken(null);

      if (response.detailedErrors?.length > 0) {
        setServerError('Kérjük, javítsa ki a megjelölt mezőkben lévő hibákat.');
      } else if (response.errorStatus === 409 || response.error?.includes('már regisztrálva')) {
        setServerError('Ez az email cím már regisztrálva van. Kérjük, jelentkezzen be, vagy használjon más email címet.');
        setError('email', { type: 'server', message: 'Ez az email cím már regisztrálva van' });
      } else if (response.errorStatus === 429) {
        setServerError('Túl sok regisztrációs kísérlet. Kérjük, várjon néhány percet, majd próbálja újra.');
      } else if (response.errorStatus >= 500) {
        setServerError('Szerver hiba történt. Kérjük, próbálja újra néhány perc múlva.');
      } else if (!response.errorStatus) {
        setServerError('Nem sikerült csatlakozni a szerverhez. Kérjük, ellenőrizze internetkapcsolatát és próbálja újra.');
      } else {
        setServerError(response.error || 'Regisztráció sikertelen. Kérjük, ellenőrizze az adatokat és próbálja újra.');
      }
    } catch (err) {
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
      setServerError('Váratlan hiba történt. Kérjük, töltse újra az oldalt és próbálja újra.');
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Helmet>
        <title>Regisztráció | Wedigo</title>
        <meta name="description" content="Regisztrálj a Wedigón: párként tervezd meg az esküvőd és foglalj szolgáltatót, szolgáltatóként pedig hozd létre ingyenes profilodat." />
        <link rel="canonical" href="https://wedigo.hu/register" />
      </Helmet>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Regisztráció
        </Typography>
        
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Hozz létre egy fiókot, hogy hozzáférj a piactér funkcióihoz
        </Typography>

        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setServerError('')}>
            {serverError}
          </Alert>
        )}

        {showValidationSummary && Object.keys(errors).length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setShowValidationSummary(false)}>
            Kérjük, javítsa ki a piros mezőkben jelzett hibákat a regisztráció folytatásához.
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit, onValidationError)}>
          {/* Felhasználó típusa – kártya-alapú választó */}
          <Controller
            name="userType"
            control={control}
            render={({ field }) => (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                  Regisztrálok mint:
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  {[
                    {
                      value: 'CLIENT',
                      icon: <PersonOutlined sx={{ fontSize: 36 }} />,
                      label: 'Ügyfél',
                      desc: 'Esküvői szolgáltatást keresek',
                    },
                    {
                      value: 'PROVIDER',
                      icon: <BusinessCenterOutlined sx={{ fontSize: 36 }} />,
                      label: 'Szolgáltató',
                      desc: 'Esküvői szolgáltatást kínálok',
                    },
                  ].map((opt) => {
                    const selected = field.value === opt.value;
                    return (
                      <Card
                        key={opt.value}
                        variant="outlined"
                        sx={{
                          flex: 1,
                          borderWidth: 2,
                          borderColor: selected ? 'primary.main' : 'divider',
                          bgcolor: selected ? 'primary.50' : 'background.paper',
                          transition: 'border-color 0.2s, background-color 0.2s',
                          opacity: isSubmitting ? 0.6 : 1,
                        }}
                      >
                        <CardActionArea
                          disabled={isSubmitting}
                          onClick={() => {
                            field.onChange(opt.value);
                            if (opt.value === 'CLIENT') {
                              setValue('businessName', '');
                              setValue('city', '');
                              setValue('zipCode', '');
                              setValue('counties', []);
                            }
                          }}
                          sx={{ p: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}
                        >
                          <Box sx={{ color: selected ? 'primary.main' : 'text.secondary' }}>
                            {opt.icon}
                          </Box>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {opt.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" align="center">
                            {opt.desc}
                          </Typography>
                        </CardActionArea>
                      </Card>
                    );
                  })}
                </Box>
                {errors.userType && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.userType.message}
                  </Typography>
                )}
              </Box>
            )}
          />

          {/* ── Személyes adatok ── */}
          <Divider sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Személyes adatok
            </Typography>
          </Divider>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Vezetéknév *"
                  {...field}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  disabled={isSubmitting}
                />
              )}
            />
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Keresztnév *"
                  {...field}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Box>

          {/* ── Hozzáférési adatok ── */}
          <Divider sx={{ mb: 2, mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Hozzáférési adatok
            </Typography>
          </Divider>
          <Box sx={{ mb: 2 }}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Email cím *"
                  type="email"
                  {...field}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Jelszó"
                  type={showPassword ? 'text' : 'password'}
                  {...field}
                  error={!!errors.password}
                  helperText={errors.password?.message || 'Min. 6 karakter, kis- és nagybetű + szám'}
                  disabled={isSubmitting}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(prev => !prev)}
                          edge="end"
                          tabIndex={-1}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              )}
            />
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Jelszó megerősítése"
                  type={showConfirmPassword ? 'text' : 'password'}
                  {...field}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  disabled={isSubmitting}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(prev => !prev)}
                          edge="end"
                          tabIndex={-1}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              )}
            />
          </Box>

          {/* ── Elérhetőség ── */}
          <Divider sx={{ mb: 2, mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Elérhetőség{' '}
              <Box component="span" sx={{ fontWeight: 400, textTransform: 'none', opacity: 0.7 }}>(opcionális)</Box>
            </Typography>
          </Divider>
          <Box>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Telefonszám"
                  {...field}
                  placeholder="+36201234567"
                  error={!!errors.phone}
                  helperText={errors.phone?.message || 'Érvényes magyar mobilszám (pl. +36201234567)'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Box>

          {/* Szolgáltató specifikus mezők */}
          {watchedUserType === 'PROVIDER' && (
            <>
              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Szolgáltatói adatok
                </Typography>
              </Divider>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Controller
                  name="businessName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      label="Vállalkozás neve *"
                      {...field}
                      error={!!errors.businessName}
                      helperText={errors.businessName?.message}
                      disabled={isSubmitting}
                    />
                  )}
                />
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        fullWidth
                        label="Város"
                        {...field}
                        error={!!errors.city}
                        helperText={errors.city?.message}
                        disabled={isSubmitting}
                      />
                    )}
                  />
                  <Controller
                    name="zipCode"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        fullWidth
                        label="Irányítószám"
                        {...field}
                        error={!!errors.zipCode}
                        helperText={errors.zipCode?.message}
                        disabled={isSubmitting}
                      />
                    )}
                  />
                </Box>
                <Controller
                  name="counties"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      options={HUNGARIAN_COUNTIES}
                      value={field.value}
                      onChange={(event, newValue) => {
                        const hasAll = newValue.includes('Magyarország teljes területe');
                        const prevHasAll = field.value.includes('Magyarország teljes területe');
                        if (hasAll && !prevHasAll) {
                          field.onChange(['Magyarország teljes területe']);
                        } else if (hasAll && prevHasAll && newValue.length > 1) {
                          field.onChange(newValue.filter(v => v !== 'Magyarország teljes területe'));
                        } else {
                          field.onChange(newValue);
                        }
                      }}
                      filterSelectedOptions
                      disabled={isSubmitting}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option}
                            size="small"
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Lefedett megye(k) *"
                          error={!!errors.counties}
                          helperText={errors.counties?.message || 'Válassza ki a lefedett megyé(ke)t, vagy "Magyarország teljes területe" ha országos'}
                        />
                      )}
                    />
                  )}
                />
              </Box>
            </>
          )}

          {/* Hírlevél feliratkozás checkbox */}
          <Box sx={{ mt: 3 }}>
            <Controller
              name="subscribedToNewsletter"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      color="primary"
                      disabled={isSubmitting}
                    />
                  }
                  label="Feliratkozom a hírlevélre"
                />
              )}
            />
          </Box>

          {/* Adatkezelési tájékoztató hozzájárulás checkbox */}
          <Box sx={{ mt: 1, p: errors.privacyConsent ? 1 : 0, borderRadius: 1, border: errors.privacyConsent ? '1px solid' : 'none', borderColor: 'error.main' }}>
            <Controller
              name="privacyConsent"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      color="primary"
                      disabled={isSubmitting}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Elolvastam és elfogadom az{' '}
                      <Link
                        to="/adatvedelmi-nyilatkozat"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'inherit', fontWeight: 600 }}
                      >
                        Adatkezelési tájékoztatót
                      </Link>
                      {' '}*
                    </Typography>
                  }
                />
              )}
            />
            {errors.privacyConsent && (
              <Typography variant="caption" color="error" sx={{ ml: 4, display: 'block' }}>
                {errors.privacyConsent.message}
              </Typography>
            )}
          </Box>

          {/* Felhasználási feltételek hozzájárulás checkbox */}
          <Box sx={{ mt: 1, p: errors.termsConsent ? 1 : 0, borderRadius: 1, border: errors.termsConsent ? '1px solid' : 'none', borderColor: 'error.main' }}>
            <Controller
              name="termsConsent"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      color="primary"
                      disabled={isSubmitting}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Elolvastam és elfogadom {watchedUserType === 'PROVIDER' ? 'a' : 'az'}{' '}
                      <Link
                        to={watchedUserType === 'PROVIDER' ? '/partneri-aszf' : '/felhasznalasi-feltetelek'}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'inherit', fontWeight: 600 }}
                      >
                        {watchedUserType === 'PROVIDER' ? 'Partneri Általános Szerződési Feltételeket' : 'Általános Szerződési Feltételeket'}
                      </Link>
                      {' '}*
                    </Typography>
                  }
                />
              )}
            />
            {errors.termsConsent && (
              <Typography variant="caption" color="error" sx={{ ml: 4, display: 'block' }}>
                {errors.termsConsent.message}
              </Typography>
            )}
          </Box>

          {/* CAPTCHA */}
          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={(token) => {
                setCaptchaToken(token);
                if (token) setCaptchaError('');
              }}
              onExpired={() => setCaptchaToken(null)}
            />
            {captchaError && (
              <Typography variant="caption" color="error">
                {captchaError}
              </Typography>
            )}
          </Box>

          {/* Regisztráció gomb */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isSubmitting}
              sx={{ minWidth: 200 }}
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'Regisztráció'}
            </Button>
          </Box>

          {/* Bejelentkezés link */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2">
              Már van fiókod?{' '}
              <Link to="/login" style={{ textDecoration: 'none' }}>
                <Button variant="text" disabled={isSubmitting}>
                  Bejelentkezés
                </Button>
              </Link>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Register;